/**
 * The engine clock. ARCHITECTURE.md §5.1.
 *
 * This is the ONLY module in src/ permitted to read a wall clock. Everything else derives
 * time from `ctx.time.simSeconds` (the fixed-step accumulation) so that a slow boot, a
 * garbage-collection pause, or a devtools breakpoint cannot change a deterministic capture.
 */

import { FIXED_STEP, MAX_FRAME_MS, MAX_STEPS_PER_FRAME } from './constants.js';

/** Monotonic wall clock in milliseconds. The single sanctioned wall-clock read. */
export const nowMs =
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? () => performance.now()
    : () => Number(process.hrtime.bigint() / 1000n) / 1000;

export class Clock {
  constructor({ deterministic = false } = {}) {
    this.tick = 0;
    this.simSeconds = 0;
    this.frame = 0;
    this.frameDt = 0;
    this.alpha = 0;
    this.timeScale = 1;
    this.droppedSteps = 0;
    this.deterministic = deterministic;

    /** Wall time of the previous sampled frame, ms. */
    this._lastWall = 0;
    this._started = false;
    this._accumulator = 0;
    /** Steps executed on the most recent frame — diagnostic. */
    this.stepsLastFrame = 0;
    /** Real seconds elapsed since start (diagnostic; never drives gameplay). */
    this.wallSeconds = 0;
  }

  /**
   * Samples the wall clock and accumulates simulation time.
   * @returns {number} number of fixed steps to run this frame
   */
  beginFrame(rawNowMs) {
    if (this.deterministic) {
      this.frameDt = FIXED_STEP;
      return 0; // deterministic runs are driven explicitly by Engine.runDeterministic
    }
    const wall = rawNowMs === undefined ? nowMs() : rawNowMs;
    if (!this._started) {
      this._started = true;
      this._lastWall = wall;
      this.frameDt = 0;
      this.stepsLastFrame = 0;
      return 0;
    }
    let deltaMs = wall - this._lastWall;
    if (deltaMs < 0) deltaMs = 0; // clock stepped backwards; ignore
    if (deltaMs > MAX_FRAME_MS) deltaMs = MAX_FRAME_MS;
    this._lastWall = wall;

    const dt = deltaMs / 1000;
    this.frameDt = dt;
    this.wallSeconds += dt;
    this._accumulator += dt * this.timeScale;

    let steps = 0;
    while (this._accumulator >= FIXED_STEP && steps < MAX_STEPS_PER_FRAME) {
      steps++;
      this._accumulator -= FIXED_STEP;
    }
    if (steps === MAX_STEPS_PER_FRAME && this._accumulator >= FIXED_STEP) {
      const shed = Math.floor(this._accumulator / FIXED_STEP);
      this.droppedSteps += shed;
      this._accumulator -= shed * FIXED_STEP;
    }
    this.stepsLastFrame = steps;
    return steps;
  }

  /** Advances the integer tick counter. Called once per fixed step by the engine. */
  advanceTick() {
    this.tick++;
    this.simSeconds = this.tick * FIXED_STEP;
  }

  /** Finalises interpolation state after all fixed steps for this frame. */
  endSteps() {
    this.alpha = this.deterministic ? 0 : this._accumulator / FIXED_STEP;
  }

  endFrame() {
    this.frame++;
  }

  /** Pauses accumulation without discarding the partial step. */
  pause() {
    this.timeScale = 0;
  }

  resume(scale = 1) {
    this.timeScale = scale;
  }

  get paused() {
    return this.timeScale === 0;
  }

  /** Resets everything except `deterministic`. Used between rounds in tools. */
  reset() {
    this.tick = 0;
    this.simSeconds = 0;
    this.frame = 0;
    this.frameDt = 0;
    this.alpha = 0;
    this.timeScale = 1;
    this.droppedSteps = 0;
    this._accumulator = 0;
    this._started = false;
    this._lastWall = 0;
    this.stepsLastFrame = 0;
    this.wallSeconds = 0;
  }

  /** Converts simulation ticks to a display string like "12.5s". */
  formatTicks(ticks) {
    return `${(ticks * FIXED_STEP).toFixed(2)}s`;
  }
}
